import { api } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, FilePdf, FileXls, TrendUp } from "@phosphor-icons/react";

export default function FinancialHighlightsTab({ doc }) {
  const pack = doc.pack;
  const fh = pack?.financial_highlights;
  if (!fh) {
    return (
      <div className="bg-card border border-border rounded-2xl p-12 text-center card-elev">
        <div className="font-display text-xl tracking-tight mb-2">No financial highlights yet</div>
        <p className="text-sm text-muted-foreground">Run AI Analysis to generate this section.</p>
      </div>
    );
  }
  const kpis = fh.kpis || [];
  const series = fh.revenue_series || [];
  const alloc = fh.funding_allocation || [];
  const maxRev = Math.max(...series.map((s) => Math.max(Number(s.actual) || 0, Number(s.projected) || 0)), 1);
  const totalAlloc = alloc.reduce((s, a) => s + (Number(a.pct) || 0), 0) || 1;

  const download = async (fmt) => {
    const t = toast.loading(`Preparing ${fmt.toUpperCase()}…`);
    try {
      const resp = await api.get(`/investment-docs/${doc.id}/export/financial-highlights.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data); const a = document.createElement("a"); a.href = url;
      a.download = `${(doc.company_name || "orixa-invest").replace(/\s+/g, "-").toLowerCase()}-financial-highlights.${fmt}`;
      a.click(); URL.revokeObjectURL(url); toast.success(`${fmt.toUpperCase()} downloaded`, { id: t });
    } catch { toast.error("Download failed", { id: t }); }
  };

  return (
    <div className="space-y-8" data-testid="idoc-tab-financial-body">
      {fh.growth_narrative && (
        <div className="bg-gradient-to-br from-primary to-[hsl(var(--accent))] text-primary-foreground rounded-2xl p-6 sm:p-8 card-elev-lg">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-2">Growth narrative</div>
          <div className="text-lg leading-relaxed max-w-3xl">{fh.growth_narrative}</div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {kpis.map((k, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4 card-elev" data-testid={`fh-kpi-${i}`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{k.label}</div>
            <div className="mt-2 text-2xl font-display tracking-tight text-primary">{k.value}</div>
            {k.hint && <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{k.hint}</div>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-2xl p-6 card-elev" data-testid="fh-revenue-chart">
          <div className="flex items-center gap-2 mb-6">
            <TrendUp size={18} weight="duotone" className="text-primary" />
            <div className="font-display text-lg tracking-tight">Revenue trajectory</div>
          </div>
          <div className="space-y-3">
            {series.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-2 text-xs font-semibold text-muted-foreground">{s.period}</div>
                <div className="col-span-10 grid grid-cols-2 gap-2">
                  <Bar label="Actual" value={s.actual} max={maxRev} tone="primary" />
                  <Bar label="Projected" value={s.projected} max={maxRev} tone="gold" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 card-elev" data-testid="fh-allocation-chart">
          <div className="font-display text-lg tracking-tight mb-6">Use of funds</div>
          <div className="space-y-3">
            {alloc.map((a, i) => {
              const pct = (Number(a.pct) || 0);
              return (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-semibold">{a.category}</span>
                    <span className="text-muted-foreground">{pct.toFixed(0)}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${(pct / totalAlloc) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {fh.capital_requirements && (
        <div className="bg-card border border-border rounded-2xl p-6 card-elev">
          <div className="text-overline text-muted-foreground mb-2">Capital requirements</div>
          <p className="text-sm leading-relaxed text-foreground">{fh.capital_requirements}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 justify-end">
        <button onClick={() => download("pdf")} className="inline-flex items-center gap-2 border border-border bg-card rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-secondary" data-testid="fh-download-pdf">
          <FilePdf size={14} weight="duotone" /> PDF
        </button>
        <button onClick={() => download("xlsx")} className="inline-flex items-center gap-2 border border-border bg-card rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-secondary" data-testid="fh-download-xlsx">
          <FileXls size={14} weight="duotone" /> XLSX
        </button>
      </div>
    </div>
  );
}

function Bar({ label, value, max, tone }) {
  const num = Number(value) || 0;
  const w = max ? Math.max(4, (num / max) * 100) : 0;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="h-4 rounded-md bg-secondary overflow-hidden mt-1">
        <div className={`h-full ${tone === "primary" ? "bg-primary" : "bg-[hsl(var(--gold))]"}`} style={{ width: `${w}%` }} />
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">{num.toLocaleString()}</div>
    </div>
  );
}
